// GeoJSON → SVG projection utilities for inline SVG rendering.
// No external dependencies — pure coordinate math.

export interface Bbox {
  minLng: number;
  maxLng: number;
  minLat: number;
  maxLat: number;
}

export type ProjectFn = (lng: number, lat: number) => [number, number];

/** Flatten all coordinate pairs from a GeoJSON geometry into a flat array. */
function flattenCoords(geom: GeoJSON.Geometry): number[][] {
  switch (geom.type) {
    case 'Point':           return [geom.coordinates as number[]];
    case 'MultiPoint':
    case 'LineString':      return geom.coordinates as number[][];
    case 'MultiLineString':
    case 'Polygon':         return (geom.coordinates as number[][][]).flat();
    case 'MultiPolygon':    return (geom.coordinates as number[][][][]).flat(2);
    default:                return [];
  }
}

/** Compute bounding box of a GeoJSON geometry. */
export function calcBbox(geom: GeoJSON.Geometry): Bbox {
  const cs = flattenCoords(geom);
  if (!cs.length) return { minLng: 0, maxLng: 1, minLat: 0, maxLat: 1 };
  const lngs = cs.map(c => c[0]);
  const lats  = cs.map(c => c[1]);
  return {
    minLng: Math.min(...lngs), maxLng: Math.max(...lngs),
    minLat: Math.min(...lats), maxLat: Math.max(...lats),
  };
}

/**
 * Returns a projection function that maps (lng, lat) → (svgX, svgY)
 * within the given viewBox dimensions, preserving aspect ratio with padding.
 */
export function makeProjector(
  bbox: Bbox,
  viewW: number,
  viewH: number,
  padding = 8,
): ProjectFn {
  const lngRange = bbox.maxLng - bbox.minLng || 1;
  const latRange  = bbox.maxLat - bbox.minLat || 1;
  const availW    = viewW - 2 * padding;
  const availH    = viewH - 2 * padding;
  const scale     = Math.min(availW / lngRange, availH / latRange);
  const renderedW = lngRange * scale;
  const renderedH = latRange * scale;
  const ox        = padding + (availW - renderedW) / 2;
  const oy        = padding + (availH - renderedH) / 2;

  return (lng: number, lat: number): [number, number] => [
    ox + (lng - bbox.minLng) * scale,
    oy + (bbox.maxLat - lat) * scale,   // Y inverted in SVG
  ];
}

/** Format a projected coordinate pair for SVG path commands. */
function pt(project: ProjectFn, c: number[]): string {
  const [x, y] = project(c[0], c[1]);
  return `${x.toFixed(2)},${y.toFixed(2)}`;
}

/** Convert a ring (closed linear ring) to an SVG path segment. */
function ringToPath(ring: number[][], project: ProjectFn): string {
  return ring.map((c, i) => `${i === 0 ? 'M' : 'L'}${pt(project, c)}`).join(' ') + ' Z';
}

/**
 * Convert a GeoJSON Polygon or MultiPolygon geometry to an SVG `d` attribute string.
 * Each sub-polygon is concatenated with a space (SVG fill-rule handles holes).
 */
export function polygonToSvgD(
  geom: GeoJSON.Polygon | GeoJSON.MultiPolygon,
  project: ProjectFn,
): string {
  if (geom.type === 'Polygon') {
    return geom.coordinates.map(ring => ringToPath(ring, project)).join(' ');
  }
  return geom.coordinates
    .map(poly => poly.map(ring => ringToPath(ring, project)).join(' '))
    .join(' ');
}

/**
 * Convert a GeoJSON LineString or MultiLineString to SVG `d` attribute.
 */
export function lineToSvgD(
  geom: GeoJSON.LineString | GeoJSON.MultiLineString,
  project: ProjectFn,
): string {
  if (geom.type === 'LineString') {
    return geom.coordinates.map((c, i) => `${i === 0 ? 'M' : 'L'}${pt(project, c)}`).join(' ');
  }
  return geom.coordinates
    .map(line => line.map((c, i) => `${i === 0 ? 'M' : 'L'}${pt(project, c)}`).join(' '))
    .join(' ');
}

/**
 * Filter a GeoJSON FeatureCollection of line features to those whose
 * coordinates fall within a square window around a center point.
 */
export function filterEdgesNearCenter(
  features: GeoJSON.Feature[],
  centerLng: number,
  centerLat: number,
  halfRadius: number,
): GeoJSON.Feature[] {
  return features.filter(f => {
    const g = f.geometry as GeoJSON.LineString | GeoJSON.MultiLineString;
    const coords: number[][] =
      g.type === 'LineString'
        ? g.coordinates
        : g.coordinates.flat();
    return coords.some(
      c => Math.abs(c[0] - centerLng) < halfRadius && Math.abs(c[1] - centerLat) < halfRadius,
    );
  });
}
